import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, MapPin, ChevronDown, CheckCircle2, QrCode, ArrowRight, Wrench, Lightbulb, Droplets, KeyRound, Sparkles, AlertOctagon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const categoriesES = [
  { id: 'electricidad', label: 'Electricidad', icon: Lightbulb },
  { id: 'fontaneria', label: 'Fontanería', icon: Droplets },
  { id: 'cerrajeria', label: 'Cerrajería', icon: KeyRound },
  { id: 'limpieza', label: 'Limpieza', icon: Sparkles },
  { id: 'estructural', label: 'Estructural', icon: AlertOctagon },
  { id: 'otros', label: 'Otros', icon: Wrench },
];

const categoriesCA = [
  { id: 'electricidad', label: 'Electricitat', icon: Lightbulb },
  { id: 'fontaneria', label: 'Lampisteria', icon: Droplets },
  { id: 'cerrajeria', label: 'Serralleria', icon: KeyRound },
  { id: 'limpieza', label: 'Neteja', icon: Sparkles },
  { id: 'estructural', label: 'Estructural', icon: AlertOctagon },
  { id: 'otros', label: 'Altres', icon: Wrench },
];

export default function VecinoFlow() {
  const { lang } = useLanguage();
  const labels = lang === 'ca' ? {
    scanTitle: 'Escaneja el codi QR del teu portal',
    scanSub: 'O simula que l’has escanejat tocant a sota.',
    scanCta: 'Simular escaneig',
    formTitle: 'Reportar incidència',
    building: 'C/ del Mar, 14 · Barcelona',
    vivienda: 'Habitatge',
    viviendaOpts: ['1r 1a', '1r 2a', '2n 1a', '2n 2a', '3r 1a', 'Zones comunes'],
    categoria: 'Categoria',
    descripcion: 'Descripció',
    descripcionPh: 'Explica què passa…',
    foto: 'Afegir foto (opcional)',
    fotoSub: 'Toca per capturar',
    enviar: 'Enviar incidència',
    successT: 'Incidència enviada',
    successD: 'L’administrador la rebrà a l’instant. Rebràs notificacions per correu electrònic quan canviï d’estat.',
    ref: 'Referència',
    nuevo: 'Reportar una altra',
    cats: categoriesCA,
  } : {
    scanTitle: 'Escanea el código QR de tu portal',
    scanSub: 'O simula que lo escaneaste tocando abajo.',
    scanCta: 'Simular escaneo',
    formTitle: 'Reportar incidencia',
    building: 'C/ del Mar, 14 · Barcelona',
    vivienda: 'Vivienda',
    viviendaOpts: ['1º A', '1º B', '2º A', '2º B', '3º A', 'Zonas comunes'],
    categoria: 'Categoría',
    descripcion: 'Descripción',
    descripcionPh: 'Cuéntanos qué pasa…',
    foto: 'Añadir foto (opcional)',
    fotoSub: 'Toca para capturar',
    enviar: 'Enviar incidencia',
    successT: 'Incidencia enviada',
    successD: 'El administrador la recibirá al instante. Recibirás notificaciones por email cuando cambie de estado.',
    ref: 'Referencia',
    nuevo: 'Reportar otra',
    cats: categoriesES,
  };

  const [step, setStep] = useState(0); // 0 scan, 1 form, 2 success
  const [vivienda, setVivienda] = useState(labels.viviendaOpts[0]);
  const [cat, setCat] = useState(labels.cats[0].id);
  const [desc, setDesc] = useState('');
  const ref = `#INC-${(Math.floor(Math.random() * 9000) + 1000).toString()}`;

  return (
    <div className="bg-alt min-h-[calc(100vh-73px)] py-12 md:py-16">
      <div className="max-w-md mx-auto px-6">
        {/* Phone frame */}
        <div className="bg-[#1A1F2B] rounded-[40px] p-3 shadow-2xl" data-testid="phone-frame">
          <div className="bg-[#1A1F2B] flex items-center justify-center pb-2">
            <div className="w-24 h-1.5 bg-black rounded-full" />
          </div>
          <div className="bg-app rounded-[28px] overflow-hidden">
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="scan"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-8 text-center min-h-[520px] flex flex-col justify-center"
                  data-testid="vecino-step-scan"
                >
                  <div className="label-eyebrow mb-3">PortalFinca</div>
                  <h2 className="font-serif text-2xl text-app mb-3">{labels.scanTitle}</h2>
                  <p className="text-sm text-app-secondary mb-8">{labels.scanSub}</p>
                  <div className="qr-mock w-44 h-44 mx-auto rounded-md mb-4 relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-app rounded-md p-2">
                        <QrCode size={24} className="text-app" />
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setStep(1)}
                    className="mt-4 inline-flex items-center justify-center gap-2 bg-primary-app text-white font-medium px-5 py-3 rounded-md hover:bg-[#8F3E2E] transition-colors"
                    data-testid="vecino-scan-cta"
                  >
                    {labels.scanCta} <ArrowRight size={16} />
                  </button>
                </motion.div>
              )}

              {step === 1 && (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-6 min-h-[520px]"
                  data-testid="vecino-step-form"
                >
                  <div className="flex items-center gap-1.5 text-xs text-app-muted mb-1">
                    <MapPin size={12} />
                    {labels.building}
                  </div>
                  <h2 className="font-serif text-2xl text-app mb-5">{labels.formTitle}</h2>

                  <div className="mb-4">
                    <label className="text-xs label-eyebrow block mb-1.5">{labels.vivienda}</label>
                    <div className="relative">
                      <select
                        value={vivienda}
                        onChange={(e) => setVivienda(e.target.value)}
                        className="w-full bg-white border border-app rounded-md px-3 py-2.5 text-sm text-app appearance-none"
                        data-testid="vecino-vivienda"
                      >
                        {labels.viviendaOpts.map((o) => (
                          <option key={o}>{o}</option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-3 text-app-muted pointer-events-none" />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-xs label-eyebrow block mb-1.5">{labels.categoria}</label>
                    <div className="grid grid-cols-3 gap-2">
                      {labels.cats.map((c) => {
                        const Icon = c.icon;
                        const active = cat === c.id;
                        return (
                          <button
                            key={c.id}
                            onClick={() => setCat(c.id)}
                            className={`flex flex-col items-center gap-1 py-2.5 rounded-md text-xs transition-colors ${
                              active
                                ? 'bg-primary-app text-white border border-primary-app'
                                : 'bg-white border border-app text-app-secondary hover:border-primary-app/40'
                            }`}
                            data-testid={`vecino-cat-${c.id}`}
                          >
                            <Icon size={16} />
                            <span>{c.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-xs label-eyebrow block mb-1.5">{labels.descripcion}</label>
                    <textarea
                      value={desc}
                      onChange={(e) => setDesc(e.target.value)}
                      placeholder={labels.descripcionPh}
                      rows={3}
                      className="w-full bg-white border border-app rounded-md px-3 py-2.5 text-sm resize-none"
                      data-testid="vecino-descripcion"
                    />
                  </div>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 bg-white border border-dashed border-app rounded-md px-3 py-3 mb-5 hover:bg-surface-muted transition-colors"
                    data-testid="vecino-foto"
                  >
                    <Camera size={18} className="text-app-muted" />
                    <div className="text-left">
                      <div className="text-sm text-app">{labels.foto}</div>
                      <div className="text-xs text-app-muted">{labels.fotoSub}</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setStep(2)}
                    className="w-full bg-primary-app text-white font-medium px-5 py-3 rounded-md hover:bg-[#8F3E2E] transition-colors"
                    data-testid="vecino-enviar"
                  >
                    {labels.enviar}
                  </button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="p-8 min-h-[520px] flex flex-col items-center justify-center text-center"
                  data-testid="vecino-step-success"
                >
                  <div className="w-20 h-20 rounded-full bg-[#3B7A57]/10 flex items-center justify-center mb-5">
                    <CheckCircle2 size={40} className="text-[#3B7A57]" />
                  </div>
                  <h2 className="font-serif text-3xl text-app mb-3">{labels.successT}</h2>
                  <p className="text-sm text-app-secondary leading-relaxed mb-5">{labels.successD}</p>
                  <div className="bg-surface-muted border border-app rounded-md px-4 py-2.5 mb-6 inline-block">
                    <span className="text-xs text-app-muted mr-2">{labels.ref}:</span>
                    <span className="font-mono text-sm text-app">{ref}</span>
                  </div>
                  <button
                    onClick={() => setStep(0)}
                    className="text-sm text-primary-app font-medium hover:underline"
                    data-testid="vecino-nuevo"
                  >
                    {labels.nuevo}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
